import { useId, useState } from "react"
import {
  Alert,
  Button,
  Chip,
  MenuItem,
  Paper,
  Popper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  type TextFieldProps,
} from "@mui/material"
import { v4 as uuid } from "uuid"
import {
  createSlotMarker,
  findSlotMarkers,
  isWholeSlotMarker,
} from "../model/slots"
import { validUrl } from "../markup/shared"
import type { SlotOccurrence } from "../types"
import { occurrenceLocation, safeDisplay, type EditorSlot } from "./linkEditing"
import { fromSourceRuns, toSourceRuns } from "./sourceTokens"

type SourceTextFieldProps = Pick<
  TextFieldProps,
  | "disabled"
  | "autoFocus"
  | "autoComplete"
  | "name"
  | "inputRef"
  | "onBlur"
  | "error"
  | "helperText"
> & {
  value: string
  onChange: (value: string) => void
  label: string
}

/** Literal runs are editable source, never redacted presentation. Only real
 * markers are atomic chips; their captions are never written back to source. */
export function SourceTextField({
  label,
  value,
  onChange,
  helperText,
  ...props
}: SourceTextFieldProps) {
  const runs = toSourceRuns(value)
  if (!runs.some((run) => run.kind === "slot"))
    return (
      <TextField
        {...props}
        size="small"
        label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        helperText={helperText}
      />
    )
  if (runs[0]?.kind !== "text") runs.unshift({ kind: "text", text: "" })
  if (runs.at(-1)?.kind !== "text") runs.push({ kind: "text", text: "" })
  return (
    <Stack spacing={1} role="group" aria-label={label}>
      <Typography variant="subtitle2">{label}</Typography>
      {runs.map((run, index) =>
        run.kind === "slot" ? (
          <Chip
            key={index}
            label={run.label}
            disabled={props.disabled}
            onDelete={
              props.disabled
                ? undefined
                : () =>
                    onChange(
                      fromSourceRuns(
                        runs.filter((_, position) => position !== index),
                      ),
                    )
            }
            sx={{ alignSelf: "flex-start" }}
          />
        ) : (
          <TextField
            {...props}
            size="small"
            key={index}
            label={`${label}, часть ${index + 1}`}
            value={run.text}
            autoFocus={props.autoFocus && index === 0}
            inputRef={index === 0 ? props.inputRef : undefined}
            onChange={(event) =>
              onChange(
                fromSourceRuns(
                  runs.map((item, position) =>
                    position === index
                      ? { kind: "text", text: event.target.value }
                      : item,
                  ),
                ),
              )
            }
          />
        ),
      )}
      <Typography variant="caption" color={props.error ? "error" : undefined}>
        {helperText}
      </Typography>
    </Stack>
  )
}

export type LinkDestination =
  | { kind: "static"; url: string }
  | { kind: "slot"; id?: string }

export function initialDestination(url: string): LinkDestination {
  return isWholeSlotMarker(url)
    ? { kind: "slot", id: findSlotMarkers(url)[0].slotId }
    : { kind: "static", url }
}

/** Called only when the complete local operation is applied. There is no slot
 * registry or persistent name: the marker's occurrence creates its identity. */
export function resolveDestination(
  destination: LinkDestination,
  create?: () => string,
): string {
  return destination.kind === "static"
    ? destination.url
    : createSlotMarker(destination.id ?? create?.() ?? uuid())
}

export interface LinkDestinationProps {
  value: LinkDestination
  onChange: (value: LinkDestination) => void
  slots?: EditorSlot[]
  disabled?: boolean
  error?: string
}

/** Slot values stay in memory. Select option values are local indices, never IDs. */
export function LinkDestinationFields({
  value,
  onChange,
  slots = [],
  disabled,
  error,
}: LinkDestinationProps) {
  const slotId = value.kind === "slot" ? value.id : undefined
  const options =
    slotId && !slots.some((slot) => slot.id === slotId)
      ? [
          ...slots,
          { id: slotId, label: `Ссылка ${slots.length + 1}`, occurrences: [] },
        ]
      : slots
  const selected = slotId
    ? String(options.findIndex((slot) => slot.id === slotId))
    : "new"
  return (
    <Stack spacing={1}>
      <ToggleButtonGroup
        exclusive
        value={value.kind}
        size="small"
        fullWidth
        aria-label="Адрес ссылки"
        onChange={(_, kind) => {
          if (kind) onChange(kind === "static" ? { kind, url: "" } : { kind })
        }}
        disabled={disabled}
        sx={{
          "& .MuiToggleButton-root": { textTransform: "none", lineHeight: 1.3 },
        }}
      >
        <ToggleButton value="static">Постоянный адрес</ToggleButton>
        <ToggleButton value="slot">Задаётся в публикации</ToggleButton>
      </ToggleButtonGroup>
      {value.kind === "slot" && options.length > 0 && (
        <TextField
          select
          label="Общая ссылка"
          size="small"
          value={selected}
          disabled={disabled}
          onChange={(event) =>
            onChange(
              event.target.value === "new"
                ? { kind: "slot" }
                : { kind: "slot", id: options[Number(event.target.value)].id },
            )
          }
        >
          <MenuItem value="new">Новая ссылка</MenuItem>
          {options.map((slot, index) => (
            <MenuItem key={index} value={String(index)}>
              {safeDisplay(slot.label) || `Ссылка ${index + 1}`}
              {options.some(
                (other) => other.id !== slot.id && other.label === slot.label,
              )
                ? ` — ссылка ${index + 1}`
                : ""}
            </MenuItem>
          ))}
        </TextField>
      )}
      {value.kind === "static" && (
        <SourceTextField
          autoComplete="off"
          label="URL"
          value={value.url}
          disabled={disabled}
          onChange={(url) => onChange({ kind: "static", url })}
          error={Boolean(error)}
          helperText={error}
        />
      )}
      {value.kind === "slot" && error && (
        <Alert severity="error">{error}</Alert>
      )}
    </Stack>
  )
}

export interface MessageLinkDialogProps {
  text: string
  url: string
  slots?: EditorSlot[]
  occurrences?: SlotOccurrence[]
  onCreateSlot?: () => string
  onSelectOccurrence?: (occurrence: SlotOccurrence) => void
  readOnly?: boolean
  onSave: (text: string, url: string) => boolean
  onClose: () => void
  error?: string
  anchor: { getBoundingClientRect: () => DOMRect }
  onRemove?: () => void
  removeLabel?: string
}

export function SlotOccurrenceList({
  occurrences,
  onSelectOccurrence,
}: {
  occurrences: SlotOccurrence[]
  onSelectOccurrence?: (occurrence: SlotOccurrence) => void
}) {
  if (!occurrences.length) return null
  return (
    <Stack spacing={1}>
      <Typography variant="caption">
        Использований: {occurrences.length}
      </Typography>
      {occurrences.map((occurrence, index) => (
        <Button
          key={index}
          disabled={!onSelectOccurrence}
          onClick={() => onSelectOccurrence?.(occurrence)}
          sx={{ justifyContent: "flex-start", overflowWrap: "anywhere" }}
        >
          {safeDisplay(occurrence.label) || `Ссылка ${index + 1}`} —{" "}
          {occurrenceLocation(occurrence)}
        </Button>
      ))}
    </Stack>
  )
}

export function MessageLinkDialog(props: MessageLinkDialogProps) {
  const titleId = useId()
  const [text, setText] = useState(props.text)
  const [display, setDisplay] = useState(props.text ? "text" : "url")
  const [destination, setDestination] = useState(() =>
    initialDestination(props.url),
  )
  const [urlError, setUrlError] = useState("")
  const save = () => {
    if (props.readOnly) return
    if (display === "text" && !text.trim()) {
      setUrlError("Введите текст ссылки")
      return
    }
    if (destination.kind === "static" && !validUrl(destination.url)) {
      setUrlError("Укажите HTTP(S) URL")
      return
    }
    try {
      if (
        props.onSave(text, resolveDestination(destination, props.onCreateSlot))
      )
        props.onClose()
    } catch {
      setUrlError("Не удалось создать ссылку")
    }
  }
  return (
    <Popper
      open
      anchorEl={props.anchor}
      placement="bottom-start"
      modifiers={[
        { name: "offset", options: { offset: [0, 8] } },
        { name: "preventOverflow", options: { padding: 16 } },
      ]}
      sx={{
        // Select menus use the modal layer and mount after this panel.
        zIndex: (theme) => theme.zIndex.modal,
        width: "min(340px, calc(100vw - 32px))",
      }}
    >
      <Paper
        role="dialog"
        aria-labelledby={titleId}
        elevation={8}
        sx={{
          p: 2,
          maxHeight: "min(560px, calc(100dvh - 32px))",
          overflowY: "auto",
          overflowWrap: "anywhere",
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault()
            event.stopPropagation()
            props.onClose()
          }
          if (
            event.key === "Enter" &&
            event.target instanceof HTMLInputElement
          ) {
            event.preventDefault()
            event.stopPropagation()
            save()
          }
        }}
      >
        <Stack spacing={1.5}>
          <Typography id={titleId} variant="subtitle2">
            Ссылка в сообщении
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={display}
            aria-label="Отображение ссылки"
            onChange={(_, mode) => {
              if (mode) setDisplay(mode)
              if (mode === "url") setText("")
            }}
            disabled={props.readOnly}
          >
            <ToggleButton value="text">Текст</ToggleButton>
            <ToggleButton value="url">Полный URL</ToggleButton>
          </ToggleButtonGroup>
          <SourceTextField
            label="Текст ссылки"
            value={text}
            onChange={(next) => {
              setText(next)
              setDisplay(next ? "text" : "url")
              setUrlError("")
            }}
            autoFocus
            disabled={props.readOnly}
          />
          <LinkDestinationFields
            value={destination}
            onChange={(next) => {
              setDestination(next)
              setUrlError("")
            }}
            slots={props.slots}
            disabled={props.readOnly}
            error={urlError}
          />
          {props.error && (
            <Alert severity="error">{safeDisplay(props.error)}</Alert>
          )}
          <SlotOccurrenceList
            occurrences={(props.occurrences ?? []).filter(
              (item) =>
                destination.kind === "slot" && item.slotId === destination.id,
            )}
            onSelectOccurrence={props.onSelectOccurrence}
          />
          {props.onRemove && (
            <Button
              color="error"
              disabled={props.readOnly}
              onClick={props.onRemove}
            >
              {props.removeLabel}
            </Button>
          )}
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button onClick={props.onClose}>Отмена</Button>
            <Button
              variant="contained"
              disabled={props.readOnly}
              onClick={save}
            >
              Готово
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Popper>
  )
}
